/**
 *
 * UploadLogo
 *
 */

import { useState } from 'react';
import { FormControl, FormLabel } from '@chakra-ui/react';
import { DragDropIcon } from 'assets/icons';
import { FileUploader } from 'react-drag-drop-files';
import { UseFormSetValue } from 'react-hook-form';

interface UploadLogoProps {
	name: string;
	setValue: UseFormSetValue<any>;
	label?: string;
	errorMessage?: string;
	required?: boolean;
	disabled?: boolean;
	value?: string;
	isEditing?: boolean;
	isReadOnly?: boolean;
}

const fileTypes = ['JPG', 'JPEG', 'PNG'];
const MAX_FILE_SIZE = 1;

const UploadLogo = ({ name, label, setValue, value, isReadOnly = false }: UploadLogoProps) => {
	const [selectedPhotoName, setSelectedPhotoName] = useState('');
	const [uploadError, setUploadError] = useState('');

	const toBase64 = (file: Blob) =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});

	const handleChange = async (file: File) => {
		setUploadError('');
		try {
			setSelectedPhotoName(file.name);
			const photoBase64 = await toBase64(file);
			setValue(name, photoBase64);
		} catch (error) {
			setUploadError('Erro ao processar a imagem');
		}
	};

	const handleTypeError = () => {
		setUploadError('Formato inválido. Use apenas JPG, JPEG ou PNG.');
	};

	const handleSizeError = () => {
		setUploadError(`Tamanho máximo permitido: ${MAX_FILE_SIZE}MB.`);
	};

	return (
		<>
			<FormControl>
				<FormLabel>{label}</FormLabel>

				{isReadOnly && value && (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							backgroundColor: '#FAFAFA',
							padding: '1rem',
							borderRadius: '1rem',
							border: '0.094rem solid #E5E5E5',
							height: 'auto',
							overflow: 'hidden',
						}}
					>
						<img src={value} alt="Logo" style={{ maxHeight: '6.25rem', maxWidth: '6.25rem' }} />
					</div>
				)}

				{!isReadOnly && value && (
					<FileUploader
						multiple={false}
						handleChange={handleChange}
						name={name}
						types={fileTypes}
						maxSize={MAX_FILE_SIZE}
						onTypeError={handleTypeError}
						onSizeError={handleSizeError}
					>
						<div
							style={{
								backgroundColor: '#FAFAFA',
								padding: '1rem',
								borderRadius: '1rem',
								border: '0.094rem dashed #E5E5E5',
								height: 'auto',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexDirection: 'column',
								cursor: 'pointer',
								overflow: 'hidden',
							}}
						>
							<img
								src={value}
								alt="Logo"
								style={{
									maxWidth: '100%',
									maxHeight: '100%',
									objectFit: 'contain',
								}}
							/>
							<p style={{ color: '#525252', fontSize: '1rem', fontWeight: '600' }}>{selectedPhotoName}</p>
							<p style={{ color: '#A3A3A3', fontSize: '0.75rem', fontWeight: '400' }}>Clique para alterar</p>
						</div>
					</FileUploader>
				)}

				{!isReadOnly && !value && (
					<FileUploader
						multiple={false}
						handleChange={handleChange}
						name={name}
						types={fileTypes}
						maxSize={MAX_FILE_SIZE}
						onTypeError={handleTypeError}
						onSizeError={handleSizeError}
					>
						<div
							style={{
								backgroundColor: '#FAFAFA',
								padding: '1rem',
								borderRadius: '1rem',
								color: '#525252',
								border: '0.094rem dashed #E5E5E5',
								height: '7rem',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexDirection: 'column',
								cursor: 'pointer',
							}}
						>
							<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
								<DragDropIcon />
								<p style={{ color: '#0EA5E9', fontSize: '1rem', fontWeight: '600', marginRight: '0.375rem' }}>
									Clique para enviar
								</p>
								<p style={{ color: '#525252', fontSize: '1rem', fontWeight: '600', marginRight: '0.375rem' }}>
									ou arraste a imagem
								</p>
							</div>
							<p style={{ color: '#A3A3A3', fontSize: '0.75rem', fontWeight: '400' }}>JPG, JPEG, PNG menos que 1MB</p>
						</div>
					</FileUploader>
				)}

				{uploadError && (
					<p
						style={{
							color: '#FF0F0F',
							fontSize: '1.25rem',
							fontWeight: '500',
							marginTop: '0.9375rem',
							fontStyle: 'italic',
							fontFamily: 'Boston',
							marginLeft: '0.5rem',
						}}
					>
						{uploadError}
					</p>
				)}
			</FormControl>
		</>
	);
};

export default UploadLogo;
